// src/components/sections/Contact.js
import React, { useState } from 'react';
import { Send, Github, Linkedin, Twitter } from 'lucide-react';
import { useTheme } from '../../context/ThemeContext';
import { useLanguage } from '../../context/LanguageContext';
import { translations } from '../../data/translations';
import { motion } from 'framer-motion';

const Contact = () => {
  const { darkMode } = useTheme();
  const { language } = useLanguage();
  const t = translations[language];
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [formStatus, setFormStatus] = useState({
    isSubmitting: false,
    isSubmitted: false,
    error: null
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setFormStatus({
      isSubmitting: true,
      isSubmitted: false,
      error: null
    });

    // Simuler l'envoi du formulaire
    setTimeout(() => {
      setFormStatus({
        isSubmitting: false,
        isSubmitted: true,
        error: null
      });
      
      // Réinitialiser le formulaire après l'envoi
      setFormData({
        name: '',
        email: '',
        subject: '',
        message: ''
      });
      
      // Réinitialiser le statut après 5 secondes
      setTimeout(() => {
        setFormStatus({
          isSubmitting: false,
          isSubmitted: false,
          error: null
        });
      }, 5000);
    }, 1500);
  };

  return (
    <section id="contact" className="py-16">
      <div className="container mx-auto px-6">
        <motion.h2 
          className="text-3xl font-bold mb-12 text-center"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
        >
          {t["contact.title"]}
        </motion.h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
          {/* Contact Form */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <div className={`p-6 rounded-lg shadow-lg ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
              <h3 className="text-xl font-semibold mb-4">{t["contact.subtitle"]}</h3>
              
              {formStatus.isSubmitted ? (
                <div className={`p-4 rounded mb-6 ${darkMode ? 'bg-green-800 text-green-200' : 'bg-green-100 text-green-800'}`}>
                  {t["contact.success"]}
                </div>
              ) : null}
              
              <form onSubmit={handleSubmit}>
                <div className="mb-4">
                  <label 
                    htmlFor="name" 
                    className={`block mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}
                  >
                    {t["contact.name"]}
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    className={`w-full px-4 py-2 rounded-lg focus:outline-none focus:ring-2 ${
                      darkMode 
                        ? 'bg-gray-700 border-gray-600 focus:ring-blue-500 text-white' 
                        : 'bg-gray-50 border-gray-300 focus:ring-blue-500'
                    }`}
                    required
                  />
                </div>
                
                <div className="mb-4">
                  <label 
                    htmlFor="email" 
                    className={`block mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}
                  >
                    {t["contact.email"]}
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    className={`w-full px-4 py-2 rounded-lg focus:outline-none focus:ring-2 ${
                      darkMode 
                        ? 'bg-gray-700 border-gray-600 focus:ring-blue-500 text-white' 
                        : 'bg-gray-50 border-gray-300 focus:ring-blue-500'
                    }`}
                    required
                  />
                </div>
                
                <div className="mb-4">
                  <label 
                    htmlFor="subject" 
                    className={`block mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}
                  >
                    {t["contact.subject"]}
                  </label>
                  <input
                    type="text"
                    id="subject"
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    className={`w-full px-4 py-2 rounded-lg focus:outline-none focus:ring-2 ${
                      darkMode 
                        ? 'bg-gray-700 border-gray-600 focus:ring-blue-500 text-white' 
                        : 'bg-gray-50 border-gray-300 focus:ring-blue-500'
                    }`}
                    required
                  />
                </div>
                
                <div className="mb-6">
                  <label 
                    htmlFor="message" 
                    className={`block mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}
                  >
                    {t["contact.message"]}
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    rows="5"
                    value={formData.message}
                    onChange={handleChange}
                    className={`w-full px-4 py-2 rounded-lg focus:outline-none focus:ring-2 ${
                      darkMode 
                        ? 'bg-gray-700 border-gray-600 focus:ring-blue-500 text-white' 
                        : 'bg-gray-50 border-gray-300 focus:ring-blue-500'
                    }`}
                    required
                  ></textarea>
                </div>
                
                <button
                  type="submit"
                  disabled={formStatus.isSubmitting}
                  className={`w-full flex items-center justify-center px-6 py-3 rounded-lg ${
                    darkMode ? 'bg-blue-600 hover:bg-blue-700' : 'bg-blue-500 hover:bg-blue-600'
                  } text-white transition-colors duration-200 ${
                    formStatus.isSubmitting ? 'opacity-70 cursor-not-allowed' : ''
                  }`}
                >
                  {formStatus.isSubmitting ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      {t["contact.sending"]}
                    </>
                  ) : (
                    <>
                      <Send className="mr-2 h-5 w-5" />
                      {t["contact.send"]}
                    </>
                  )}
                </button>
              </form>
            </div>
          </motion.div>
          
          {/* Contact Info */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <div className={`p-6 rounded-lg shadow-lg ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
              <h3 className="text-xl font-semibold mb-6">{t["contact.info"]}</h3>
              
              <div className="space-y-4 mb-8">
                <div className="flex items-start">
                  <div className={`p-2 rounded-full ${darkMode ? 'bg-gray-700' : 'bg-gray-100'} mr-4`}>
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path>
                    </svg>
                  </div>
                  <div>
                    <h4 className={`font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>{t["contact.phone"]}</h4>
                    <p className={darkMode ? 'text-blue-400' : 'text-blue-600'}>+237 657 13 11 93</p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <div className={`p-2 rounded-full ${darkMode ? 'bg-gray-700' : 'bg-gray-100'} mr-4`}>
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
                      <polyline points="22,6 12,13 2,6"></polyline>
                    </svg>
                  </div>
                  <div>
                    <h4 className={`font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Email</h4>
                    <p className={darkMode ? 'text-blue-400' : 'text-blue-600'}>sibpro61@gmail.com</p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <div className={`p-2 rounded-full ${darkMode ? 'bg-gray-700' : 'bg-gray-100'} mr-4`}>
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
                      <circle cx="12" cy="10" r="3"></circle>
                    </svg>
                  </div>
                  <div>
                    <h4 className={`font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>{t["contact.location"]}</h4>
                    <p className={darkMode ? 'text-gray-400' : 'text-gray-600'}>Yaoundé, Cameroun</p>
                  </div>
                </div>
              </div>
              
              <h4 className="font-medium mb-4">{t["contact.followMe"]}</h4>
              <div className="flex space-x-4">
                <a 
                  href="https://github.com/igorvannel" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className={`p-3 rounded-full ${
                    darkMode ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-100 hover:bg-gray-200'
                  } transition-colors duration-200`}
                >
                  <Github className="h-5 w-5" />
                </a>
                
                <a 
                  href="https://www.linkedin.com/in/igor-vannel-sibemou-tientcheu-353911294/" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className={`p-3 rounded-full ${
                    darkMode ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-100 hover:bg-gray-200'
                  } transition-colors duration-200`}
                >
                  <Linkedin className="h-5 w-5" />
                </a>
                
                <a 
                  href="#" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className={`p-3 rounded-full ${
                    darkMode ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-100 hover:bg-gray-200'
                  } transition-colors duration-200`}
                >
                  <Twitter className="h-5 w-5" />
                </a>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Contact;